import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, MessageSquare, BellRing, Smartphone, ShieldAlert, Wifi, BarChart3, Users, Send } from 'lucide-react';
import './exemplos.css';

export const PoliticoLandingPage: React.FC = () => {
  const [activeMessage, setActiveMessage] = useState<number>(0);

  // Auto cycle messages
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveMessage(prev => prev >= 2 ? 0 : prev + 1);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const messages = [
    { 
      id: 0, 
      type: 'WHATSAPP', 
      audiencia: '100.000 Famílias',
      content: 'A nova reforma da Escola Municipal João Goulart foi concluída! Seu filho terá ar condicionado em sala. Como você avalia esta obra?',
      poll: ['Excelente', 'Bom', 'Regular']
    },
    { 
      id: 1, 
      type: 'PUSH NOTIFICATION', 
      audiencia: '50.000 Mães',
      content: 'Atenção: A campanha de vacinação contra a gripe ocorrerá amanhã na porta do colégio. Traga a carteirinha!',
      poll: null
    },
    { 
      id: 2, 
      type: 'WHATSAPP', 
      audiencia: '30.000 Eleitores/Pais',
      content: 'O kit escolar 2026 já está disponível para retirada. É a Prefeitura trabalhando pelo futuro das nossas crianças. Um abraço do Prefeito!',
      poll: ['Confirmar Retirada', 'Dúvidas']
    }
  ];

  const currentMsg = messages[activeMessage];

  return (
    <div className="min-h-screen bg-[#020617] text-slate-200 font-sans overflow-hidden relative">
      {/* Back button */}
      <div className="absolute top-6 left-6 z-50">
        <Link to="/exemplos" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
          <ArrowLeft size={16} />
          <span className="text-sm font-semibold tracking-wider uppercase">Voltar ao Nexo</span>
        </Link>
      </div>

      {/* Radar Background */}
      <div className="absolute inset-0 flex items-center justify-center opacity-30 pointer-events-none">
        <div className="relative w-[1000px] h-[1000px] rounded-full border border-slate-800 flex items-center justify-center">
          <div className="absolute w-[800px] h-[800px] rounded-full border border-slate-800"></div>
          <div className="absolute w-[600px] h-[600px] rounded-full border border-amber-500/10"></div>
          <div className="absolute inset-0 bg-[conic-gradient(from_0deg,transparent_0deg,rgba(245,158,11,0.15)_90deg,transparent_90deg)] rounded-full animate-[spin_6s_linear_infinite]"></div>
        </div>
      </div>

      <div className="relative z-10 w-full min-h-screen flex flex-col xl:flex-row">
        
        {/* Left Panel: Executive Summary */}
        <div className="w-full xl:w-5/12 min-h-screen bg-slate-950/80 backdrop-blur-xl border-r border-white/5 p-8 md:p-16 flex flex-col justify-center">
          <motion.div initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }}>
            <div className="flex items-center gap-2 mb-8 text-amber-500">
              <Smartphone size={24} className="animate-pulse" />
              <span className="text-xs font-black tracking-[0.3em] uppercase">Gabinete de Comunicação em Massa</span>
            </div>
            
            <h1 className="text-5xl md:text-6xl font-black text-white leading-none mb-6 tracking-tighter">
              Prefeito <br/> <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-600">Conectado.</span>
            </h1>
            
            <p className="text-lg md:text-xl text-slate-300 font-light mb-6 leading-relaxed">
              Fale diretamente com <span className="text-amber-400 font-bold">100 mil eleitores</span> em 1 segundo.
            </p>

            <p className="text-sm text-slate-400 leading-relaxed mb-10 font-light border-l-2 border-amber-500/30 pl-4">
              O Aluno Conectado não é apenas segurança. É o canal direto entre a Prefeitura e as famílias. Envie notificações Push e mensagens no WhatsApp com a sua marca, sua foto, comunique melhorias, faça votações e descubra os desejos do público. 
            </p>

            <div className="grid grid-cols-2 gap-4 mb-10">
              <div className="bg-white/5 border border-white/10 p-4 rounded-xl">
                <p className="text-3xl font-light text-white flex items-center gap-2"><MessageSquare size={20} className="text-green-500"/> 98%</p>
                <p className="text-[10px] uppercase text-slate-400 tracking-wider mt-1">Taxa de Abertura WhatsApp</p>
              </div>
              <div className="bg-white/5 border border-white/10 p-4 rounded-xl">
                <p className="text-3xl font-light text-white flex items-center gap-2"><BarChart3 size={20} className="text-amber-500"/> 10k+</p>
                <p className="text-[10px] uppercase text-slate-400 tracking-wider mt-1">Votos por Enquete</p>
              </div>
            </div>

            <button className="w-full py-5 px-6 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-400 hover:to-orange-500 text-slate-950 font-black text-lg rounded-none skew-x-[-5deg] transform hover:scale-[1.02] transition-all shadow-[0_0_30px_rgba(245,158,11,0.3)]">
              <span className="skew-x-[5deg] block flex items-center justify-center gap-2">
                <Send size={20} /> Disparar Campanha Oficial
              </span>
            </button>
          </motion.div>
        </div>

        {/* Right Panel: Holographic Broadcast Interface */}
        <div className="w-full xl:w-7/12 min-h-screen p-8 md:p-16 flex flex-col justify-center items-center relative">
          
          <div className="absolute top-10 right-10 flex items-center gap-4">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-500/10 border border-amber-500/20 rounded text-amber-400 text-[10px] font-mono tracking-widest uppercase">
              <Users size={12} />
              <span>Audiência Ativa: 145.203</span>
            </div>
          </div>

          {/* Broadcast Central Node */}
          <div className="relative w-full max-w-lg">
            
            {/* Mayor Persona & Sender ID */}
            <div className="flex justify-center mb-8 relative z-20">
               <div className="bg-slate-900/90 backdrop-blur-xl border border-amber-500/30 p-4 rounded-2xl flex items-center gap-4 shadow-2xl">
                 <div className="w-16 h-16 rounded-full bg-gradient-to-tr from-amber-500 to-orange-600 p-[2px]">
                   <div className="w-full h-full rounded-full bg-slate-800 flex items-center justify-center overflow-hidden relative">
                      {/* Fake Mayor Photo Placeholder */}
                      <div className="absolute bottom-0 w-10 h-12 bg-amber-200/20 rounded-t-full"></div>
                      <div className="absolute top-2 w-6 h-6 bg-amber-200/20 rounded-full"></div>
                   </div>
                 </div>
                 <div>
                   <h3 className="text-white font-bold uppercase tracking-wider text-sm">Gabinete do Prefeito</h3>
                   <p className="text-amber-500 text-xs flex items-center gap-1"><ShieldAlert size={12}/> Conta Verificada Oficial</p>
                 </div>
               </div>
            </div>

            {/* Simulated Mobile Device screen showing the message */}
            <div className="bg-slate-900/50 backdrop-blur-2xl border border-white/10 rounded-[2.5rem] p-4 shadow-[0_20px_50px_rgba(0,0,0,0.5)] relative z-10 mx-auto w-full max-w-sm h-[550px] flex flex-col">
              {/* Notch */}
              <div className="w-32 h-6 bg-black rounded-b-xl mx-auto absolute top-0 left-1/2 -translate-x-1/2"></div>
              
              <div className="pt-8 flex-1 flex flex-col relative">
                <AnimatePresence mode="wait">
                  <motion.div 
                    key={currentMsg.id}
                    initial={{ opacity: 0, scale: 0.9, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.9, y: -20 }}
                    transition={{ duration: 0.4 }}
                    className="flex-1 flex flex-col"
                  >
                    {/* Targeting info */}
                    <div className="text-center mb-6">
                      <span className="bg-green-500/20 text-green-400 text-[10px] uppercase tracking-widest px-3 py-1 rounded-full font-bold border border-green-500/30">
                        Disparo para: {currentMsg.audiencia}
                      </span>
                    </div>

                    {/* Chat Bubble */}
                    <div className={`p-4 rounded-2xl mb-4 shadow-lg ${currentMsg.type === 'WHATSAPP' ? 'bg-green-900/40 border border-green-500/30 rounded-tl-none' : 'bg-slate-800 border border-slate-600 rounded-2xl'}`}>
                      <div className="flex items-center gap-2 mb-2">
                        {currentMsg.type === 'WHATSAPP' ? <MessageSquare size={14} className="text-green-400" /> : <BellRing size={14} className="text-amber-400" />}
                        <span className="text-[10px] font-bold text-slate-400">{currentMsg.type}</span>
                      </div>
                      <p className="text-white text-sm leading-relaxed">
                        {currentMsg.content}
                      </p>
                    </div>

                    {/* Poll/Interactive Buttons */}
                    {currentMsg.poll && (
                      <div className="space-y-2 mt-2">
                        {currentMsg.poll.map((option, i) => (
                          <div key={i} className="w-full bg-cyan-900/20 border border-cyan-500/30 text-cyan-300 text-sm py-3 px-4 rounded-xl text-center font-bold hover:bg-cyan-500/20 cursor-pointer transition-colors">
                            {option}
                          </div>
                        ))}
                      </div>
                    )}
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>

            {/* Glowing lines connecting mayor to phone */}
            <div className="absolute top-16 left-1/2 -translate-x-1/2 w-[2px] h-32 bg-gradient-to-b from-amber-500 to-transparent z-0"></div>

          </div>
        </div>
      </div>
    </div>
  );
};
